import React, { useEffect, useState } from 'react'

import { Spinner, useIsDesktop } from '@rewardops/ui'
import { ResponseItem } from '@rewardops/utils'

import { convert1DArrayInto2DRows } from '~/utils/convert-1d-array-to-2d-rows'
import { FeaturedCarouselType } from '~/app/common/enums'
import { CardSize } from '~/app/common/enums/card-size-type'
import { useTranslation } from '~/app/i18n/client'
import { useUICustomizations } from '../hooks/use-ui-customizations'
import ItemCard from '../maple/item-card'
import { RetailerCard } from '../maple/pages/retailers/retailers-card'
import ResponsiveComponent from '../responsive-component'
import {
  TransitionBtn,
  TranstionBtnDirection,
} from './featured-carousel-transition-btns'

const Loading = () => <Spinner className="mx-auto mt-4" />

type FeaturedCarouselProps = {
  type: FeaturedCarouselType
  title: string
  helperText: string
  items: ResponseItem[]
  loading: boolean
  listName?: string
}

const DEFAULT_ITEMS_ALLOWED_PER_PAGE_ON_CAROUSEL = 4
const DEFAULT_ROWS = 1

const FeaturedCarousel: React.FC<FeaturedCarouselProps> = ({
  title,
  helperText,
  items,
  loading,
  type,
  listName = 'featured_carousel',
}: FeaturedCarouselProps) => {
  const [itemsToShow, setItemsToShow] = useState<ResponseItem[]>([])
  const [page, setPage] = useState<number>(1)
  const [itemsAllowedPerPage, setItemsAllowedPerPage] = useState<number>(
    DEFAULT_ITEMS_ALLOWED_PER_PAGE_ON_CAROUSEL,
  )
  const { t } = useTranslation()

  const isDesktop = useIsDesktop()

  const updateItemsToShow = () => {
    const firstIndex =
      page === 1 ? 0 : page * itemsAllowedPerPage - itemsAllowedPerPage
    const lastIndex = firstIndex + itemsAllowedPerPage
    let selectedItems = items?.slice(firstIndex, lastIndex)

    if (selectedItems?.length < itemsAllowedPerPage && page > 1) {
      const diff = itemsAllowedPerPage - selectedItems?.length
      if (items?.length) {
        const loopedElements = items?.slice(0, diff)
        selectedItems?.push(...loopedElements)
      }
    }

    setItemsToShow(selectedItems)
  }

  const { itemsAllowed, rowsAllowed, pageConfig } = useUICustomizations({
    defaultRows: DEFAULT_ROWS,
    defaultItemsAllowed: DEFAULT_ITEMS_ALLOWED_PER_PAGE_ON_CAROUSEL,
    setItemsAllowedPerPage,
    isRetailer: type === FeaturedCarouselType.Retailer,
  })

  useEffect(() => {
    if (items?.length) {
      updateItemsToShow()
    }
  }, [items])

  useEffect(() => {
    if (!isDesktop) {
      setItemsAllowedPerPage(2)
    }

    if (isDesktop) {
      if (type === FeaturedCarouselType.Retailer) {
        setItemsAllowedPerPage(itemsAllowed * rowsAllowed)
      } else {
        setItemsAllowedPerPage(DEFAULT_ITEMS_ALLOWED_PER_PAGE_ON_CAROUSEL)
      }
    }
  }, [isDesktop])

  useEffect(() => {
    updateItemsToShow()
  }, [page, itemsAllowedPerPage])

  const goNext = () => {
    if (page == Math.ceil(items?.length / itemsAllowedPerPage)) {
      return setPage(1)
    }
    setPage(page + 1)
  }

  const goPrev = () => {
    if (page == 1) {
      return setPage(Math.ceil(items?.length / itemsAllowedPerPage))
    }

    setPage(page - 1)
  }
  if (!loading && !items?.length) {
    return null
  }

  const dekstopComponent = (
    <div
      className={`overflow-y-hiddenp-10 col-span-2 mb-[100px] flex flex-col`}
    >
      <div className="mb-12 flex flex-col items-center text-center">
        <div className="pb-4 text-[40px]">{title}</div>
        {helperText ? (
          <div className="text-base text-[#505050]">{helperText}</div>
        ) : null}
      </div>

      {loading ? (
        <Loading />
      ) : (
        <>
          <div className="flex justify-center">
            <div className={`flex items-center justify-center gap-8`}>
              {items?.length > itemsAllowedPerPage ? (
                <div className="relative h-full">
                  <div className="absolute left-0 top-[40%] z-20">
                    <TransitionBtn
                      onClick={goPrev}
                      direction={TranstionBtnDirection.Left}
                      ariaLabel={t('ariaLabel.carouselPreviousButton')}
                    />
                  </div>
                </div>
              ) : null}

              {itemsToShow.map((item: ResponseItem, index: number) => (
                <React.Fragment key={`${item?.id}-${index}`}>
                  {type === FeaturedCarouselType.Product ? (
                    <div className={`w-[261px] shrink-0`}>
                      <ItemCard
                        key={item.id}
                        item={item}
                        position={index}
                        listName={listName}
                      />
                    </div>
                  ) : null}
                </React.Fragment>
              ))}

              {type === FeaturedCarouselType.Retailer ? (
                <div
                  className={`grid gap-8`}
                  style={{
                    gridTemplateColumns: `repeat(${
                      itemsAllowedPerPage / rowsAllowed
                    }, minmax(0, 1fr))`,
                  }}
                >
                  {convert1DArrayInto2DRows(
                    itemsToShow,
                    itemsAllowed * rowsAllowed,
                  )?.map((items: ResponseItem[]) =>
                    items?.map((item: ResponseItem, index: number) => (
                      <RetailerCard
                        key={item.id}
                        item={item}
                        position={index}
                        listName={listName}
                        size={
                          (pageConfig?.featuredCarousel
                            ?.cardSize as CardSize) ?? CardSize.Large
                        }
                      />
                    )),
                  )}
                </div>
              ) : null}

              {items?.length > itemsAllowedPerPage ? (
                <div className="relative h-full">
                  <div className="absolute right-0 top-[40%] z-20">
                    <TransitionBtn
                      onClick={goNext}
                      direction={TranstionBtnDirection.Right}
                      ariaLabel={t('ariaLabel.carouselNextButton')}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </>
      )}
    </div>
  )

  const mobileComponent = (
    <div className={`overflow-y-hiddenp-10 col-span-2 mb-[60px] flex flex-col`}>
      <div className="mb-6 flex flex-col items-center text-center">
        <div className="pb-4 text-[40px]">{title}</div>
        <div className="text-base text-[#505050]">{helperText}</div>
      </div>

      {loading ? (
        <Loading />
      ) : (
        <>
          {items?.length > itemsAllowedPerPage ? (
            <div className="mb-8 flex justify-center gap-6">
              <TransitionBtn
                onClick={goPrev}
                direction={TranstionBtnDirection.Left}
                ariaLabel={t('ariaLabel.carouselPreviousButton')}
              />
              <TransitionBtn
                onClick={goNext}
                direction={TranstionBtnDirection.Right}
                ariaLabel={t('ariaLabel.carouselNextButton')}
              />
            </div>
          ) : null}

          <div className="flex justify-center">
            <div className={`flex items-center justify-center gap-0`}>
              {itemsToShow.map((item: ResponseItem, index: number) => (
                <React.Fragment key={`${item?.id}-${index}`}>
                  {type === FeaturedCarouselType.Product ? (
                    <div className={`w-[180px] shrink-0`}>
                      <ItemCard
                        key={item.id}
                        item={item}
                        position={index}
                        listName={listName}
                      />
                    </div>
                  ) : (
                    <div className={`w-[180px] shrink-0 sm:w-[260px]`}>
                      <RetailerCard
                        key={item.id}
                        item={item}
                        position={index}
                        listName={listName}
                      />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )

  return (
    <ResponsiveComponent
      desktopComponent={dekstopComponent}
      mobileComponent={mobileComponent}
    />
  )
}

export { FeaturedCarousel }
